import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GripVertical } from 'lucide-react';
import { type DragEvent, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Badge, PageHeader, SearchInput, Spinner } from '@/components/ui/layout';
import { ApiError, api } from '@/lib/api';
import { t } from '@/lib/i18n';
import { type ListParams, useAll } from '@/lib/resources';
import type { Bus, Zone } from '@/lib/types';

const OUT_OF_SERVICE = 'none';
const busParams: ListParams = { sort: 'name' };

const status = (b: Bus) =>
  !b.zone ? null : !b.is_visible ? (
    <Badge tone="amber">{t('Hidden')}</Badge>
  ) : b.is_active ? (
    <Badge tone="green">{t('On route')}</Badge>
  ) : (
    <Badge tone="blue">{t('At terminus')}</Badge>
  );

/**
 * "Arrangement": every bus as a card in the column of its zone. Drag a card to another column (or use
 * its menu) to assign the bus to that zone; "Out of service" removes it from the app.
 */
export function ArrangementPage() {
  const zones = useAll<Zone>('zones', { sort: 'sort' });
  const buses = useAll<Bus>('buses', busParams);
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [over, setOver] = useState<string | null>(null);
  const [moving, setMoving] = useState<string | null>(null);

  const assign = useMutation({
    mutationFn: ({ bus, zone }: { bus: Bus; zone: string | null }) =>
      api<Bus>(`/buses/${bus._id}/zone`, { method: 'PUT', body: { zone } }),
    onMutate: ({ bus }) => setMoving(bus._id),
    onSuccess: (updated, { bus }) => {
      qc.invalidateQueries({ queryKey: ['buses'] });
      toast.success(
        `${bus.name || bus.arvento_id} → ${updated.zone ? updated.zone.name : t('out of service')}`,
      );
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('Could not move the bus')),
    onSettled: () => setMoving(null),
  });

  const columns = useMemo(() => {
    const term = q.trim().toLowerCase();
    const match = (b: Bus) =>
      !term || [b.name, b.arvento_id, b.number].some((v) => v?.toLowerCase().includes(term));
    const all = (buses.data?.items ?? []).filter(match);
    return [
      {
        id: OUT_OF_SERVICE,
        title: t('Out of service'),
        color: '#64748b',
        sign: '',
        active: true,
        buses: all.filter((b) => !b.zone),
      },
      ...(zones.data?.items ?? []).map((z) => ({
        id: z._id,
        title: z.name,
        color: z.color,
        sign: z.bus_sign,
        active: z.is_active,
        buses: all.filter((b) => b.zone?._id === z._id),
      })),
    ];
  }, [zones.data, buses.data, q]);

  if (zones.isLoading || buses.isLoading) return <Spinner />;

  const move = (busId: string, target: string) => {
    const bus = buses.data?.items.find((b) => b._id === busId);
    if (!bus || (bus.zone?._id ?? OUT_OF_SERVICE) === target) return;
    assign.mutate({ bus, zone: target === OUT_OF_SERVICE ? null : target });
  };
  const drop = (target: string) => (e: DragEvent) => {
    e.preventDefault();
    setOver(null);
    move(e.dataTransfer.getData('text/bus-id'), target);
  };

  return (
    <>
      <PageHeader
        title={t('Arrangement')}
        subtitle={t(
          'Drag a bus to a line to put it on that line; drag it to “Out of service” to take it off the app.',
        )}
        actions={<SearchInput value={q} onChange={setQ} placeholder={t('Find a bus…')} />}
      />
      <div className="flex gap-4 overflow-x-auto pb-4">
        {columns.map((col) => (
          <section
            key={col.id}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(col.id);
            }}
            onDragLeave={() => setOver((o) => (o === col.id ? null : o))}
            onDrop={drop(col.id)}
            className={`flex w-64 shrink-0 flex-col rounded-lg border bg-slate-100/70 ${over === col.id ? 'border-brand-600 ring-2 ring-brand-600/20' : 'border-slate-200'}`}
            aria-label={col.title}
          >
            <header className="rounded-t-lg px-3 py-2 text-white" style={{ background: col.color }}>
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-semibold">
                  {col.sign && `${col.sign} · `}
                  {col.title}
                </span>
                <span className="rounded bg-black/20 px-1.5 text-xs">{col.buses.length}</span>
              </div>
              {!col.active && <div className="text-[11px] opacity-90">{t('Line not in service')}</div>}
            </header>
            <ul className="flex min-h-24 flex-1 flex-col gap-2 p-2">
              {col.buses.map((bus) => (
                <li
                  key={bus._id}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/bus-id', bus._id);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  className={`group cursor-grab rounded-md border border-slate-200 bg-white p-2 shadow-sm active:cursor-grabbing ${moving === bus._id ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-start gap-1.5">
                    <GripVertical className="mt-0.5 size-4 shrink-0 text-slate-300 group-hover:text-slate-500" />
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/buses/${bus._id}`}
                        className="block truncate text-sm font-medium hover:underline"
                      >
                        {bus.name || bus.arvento_id}
                      </Link>
                      <div className="truncate text-xs text-slate-500">{bus.arvento_id}</div>
                      {bus.description && (
                        <div className="mt-0.5 line-clamp-2 text-xs text-slate-500">{bus.description}</div>
                      )}
                      <div className="mt-1.5 flex items-center justify-between gap-1">
                        {status(bus)}
                        <select
                          aria-label={t('Move {name}', { name: bus.name || bus.arvento_id || '' })}
                          value={col.id}
                          onChange={(e) => move(bus._id, e.target.value)}
                          className="ml-auto max-w-28 rounded border border-transparent bg-transparent text-xs text-slate-500 hover:border-slate-300 focus:border-brand-600 focus:outline-none"
                        >
                          {columns.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.id === col.id ? t('Move to…') : `${c.sign ? `${c.sign} · ` : ''}${c.title}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
              {col.buses.length === 0 && (
                <li className="py-6 text-center text-xs text-slate-400">{t('Drop buses here')}</li>
              )}
            </ul>
          </section>
        ))}
      </div>
    </>
  );
}
